import React, { createContext, useContext, useCallback } from 'react'
import { useQueryState } from 'nuqs'
import dayjs from 'dayjs'

interface TimeRange {
  startDate: string // ISO format
  endDate: string // ISO format
}

interface TimeRangeContextProps extends TimeRange {
  setRange: (range: Partial<TimeRange>) => void
  setPreset: (preset: 'today' | 'day' | 'week' | 'month' | 'year') => void
  tick: 'day' | 'month'
}

const TimeRangeContext = createContext<TimeRangeContextProps | undefined>(undefined)

export const TimeRangeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // sync with URL query params using nuqs
  const [startDate, setStartDate] = useQueryState('start', {
    shallow: false,
    defaultValue: dayjs().format('YYYY-MM-DD')
  })
  const [endDate, setEndDate] = useQueryState('end', {
    shallow: false,
    defaultValue: dayjs().format('YYYY-MM-DD')
  })

  const setRange = useCallback(
    (range: Partial<TimeRange>) => {
      if (range.startDate !== undefined) setStartDate(range.startDate)
      if (range.endDate !== undefined) setEndDate(range.endDate)
    },
    [setStartDate, setEndDate]
  )

  const setPreset = useCallback(
    (preset: 'today' | 'day' | 'week' | 'month' | 'year') => {
      const now = dayjs()
      let start: dayjs.Dayjs
      switch (preset) {
        case 'today':
          start = now
          break
        case 'day':
          start = now.subtract(1, 'day')
          break
        case 'week':
          start = now.subtract(1, 'week')
          break
        case 'month':
          start = now.subtract(1, 'month')
          break
        case 'year':
          start = now.subtract(1, 'year')
          break
        default:
          start = now
      }
      setStartDate(start.format('YYYY-MM-DD').toString())
      setEndDate(now.format('YYYY-MM-DD').toString())
    },
    [setStartDate, setEndDate]
  )

  const tick =
    startDate && endDate
      ? dayjs(endDate).diff(dayjs(startDate), 'day') > 31
        ? 'month'
        : 'day'
      : 'day'

  const contextValue: TimeRangeContextProps = {
    startDate,
    endDate,
    setRange,
    setPreset,
    tick
  }

  return <TimeRangeContext.Provider value={contextValue}>{children}</TimeRangeContext.Provider>
}

export const useTimeRange = (): TimeRangeContextProps => {
  const ctx = useContext(TimeRangeContext)
  if (!ctx) {
    throw new Error('useTimeRange must be used within a TimeRangeProvider')
  }
  return ctx
}
